import {
  ArrowLeftOutlined,
  ArrowsAltOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  ShrinkOutlined
} from "@ant-design/icons";
import type {
  CellValueChangedEvent,
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  ValueSetterParams
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useApolloClient } from "@apollo/client";
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Segmented,
  Spin,
  Tooltip,
  Typography,
  message,
  type MenuProps
} from "antd";
import type { InputRef } from "antd";
import { useAgGridTreeExpandToggle } from "hooks/use-ag-grid-tree-expand-toggle";
import AntdTextCellEditor from "components/cell-editor/antd-text-cell-editor";
import ClassificationSearchCellEditor, {
  ClassificationSearchSelect,
  type ClassificationSearchSelection
} from "components/cell-editor/classification-search-cell-editor";
import {
  CellChilderenRendererWithEditButton,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode
} from "react";
import { ClassBadge } from "pages/design-scope-page/components/scope-ui";
import { SubscriptionContext } from "context/SubscriptionProvider";
import type { ProjectSettingType } from "context/ProjectProvider";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import {
  PRINT_VIEW_STORAGE_KEY,
  type DesignScopeCatalogPrintViewState
} from "pages/print-view-layout/model";
import { DateUtils } from "utils/dateutils";
import { downloadStringAsCSVFile } from "utils/utils";
import "./bootstrap-design-scope.scss";
import {
  addDesignScopeCatalogItem,
  deleteDesignScopeCatalogItem,
  downloadDesignScopeImportTemplate,
  listDesignScopeCatalogItems,
  updateDesignScopeCatalog,
  updateDesignScopeCatalogItem,
  type DesignScopeCatalog,
  type DesignScopeCatalogItem,
  type DesignScopeCatalogItemClassification,
  type DesignScopeCatalogItemUpdate
} from "./apis";
import {
  CATALOG_TREE_SYSTEM_LABEL,
  CATALOG_UNCLASSIFIED,
  catalogItemDataPath,
  decodeCatalogSeg,
  placeCatalogItemsBeforeNestedGroups,
  type CatalogTreeSystem
} from "./catalog-items-hierarchy";
import {
  designScopeCatalogExportEventType,
  designScopeCatalogPdfFileBaseName
} from "./export-design-scope-catalog-audit";
import {
  buildDesignScopeItemsCsv,
  designScopeItemsExportFilename,
  instructionLinesFromImportTemplate
} from "./export-design-scope-items-csv";
import {
  DESIGN_SCOPE_ACTION_ERROR,
  designScopeActionErrorMessage,
  DESIGN_SCOPE_ITEM_ADDED,
  DESIGN_SCOPE_ITEM_DELETED,
  DESIGN_SCOPE_LIST_DESCRIPTION_UPDATED,
  DESIGN_SCOPE_LIST_TITLE_UPDATED,
  scopeItemUpdatedMessage
} from "./design-scope-toast-messages";
import ImportCatalogItemsDrawer from "./import-catalog-items-drawer";

const CATALOG_EXPORT_EXCLUDED_COLUMNS = ["actions"];

type CatalogItemsViewProps = {
  catalog: DesignScopeCatalog;
  onBack: () => void;
  onCatalogUpdated: (patch: {
    name: string;
    description: string | null;
  }) => void;
};

type ScopeItemRow = DesignScopeCatalogItem & {
  displayId: number;
};

function childGroupNodes(node?: IRowNode<ScopeItemRow> | null) {
  return (node?.childrenAfterGroup ?? []).filter((child) => child.group);
}

function descendantGroupNodes(node: IRowNode<ScopeItemRow>) {
  const groups: IRowNode<ScopeItemRow>[] = [];
  const walk = (current: IRowNode<ScopeItemRow>) => {
    for (const child of childGroupNodes(current)) {
      groups.push(child);
      walk(child);
    }
  };
  walk(node);
  return groups;
}

/**
 * Same rule as Design Scope log: only when this group has nested group
 * children (not only leaf items). Click expands/collapses the whole subtree.
 */
function CatalogGroupDepthToggle({
  node,
  onToggleOneLevel
}: {
  node: IRowNode<ScopeItemRow>;
  onToggleOneLevel: (node: IRowNode<ScopeItemRow>) => void;
}) {
  const [expanded, setExpanded] = useState(Boolean(node.expanded));

  useEffect(() => {
    setExpanded(Boolean(node.expanded));
    const sync = () => setExpanded(Boolean(node.expanded));
    node.addEventListener?.("expandedChanged", sync);
    return () => node.removeEventListener?.("expandedChanged", sync);
  }, [node]);

  if (childGroupNodes(node).length === 0) return null;

  const toggleImmediate = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const nextExpanded = !node.expanded;
    onToggleOneLevel(node);
    setExpanded(nextExpanded);
  };

  return (
    <Tooltip title={expanded ? "Collapse Section" : "Expand Section"}>
      <Button
        type="text"
        size="small"
        className="catalog-group-depth-toggle"
        data-testid="catalog-group-depth-toggle"
        data-expanded={expanded ? "true" : "false"}
        icon={expanded ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={toggleImmediate}
      />
    </Tooltip>
  );
}

function catalogItemPatchForCell(
  colId: string,
  event: CellValueChangedEvent<ScopeItemRow>
): DesignScopeCatalogItemUpdate | null {
  const row = event.data;
  if (!row) return null;
  if (colId === "title") {
    const title = row.title;
    if (title === String(event.oldValue ?? "")) return null;
    return { title };
  }
  if (colId === "description") {
    const description = row.description ?? "";
    if (description === String(event.oldValue ?? "")) return null;
    return { description };
  }
  if (colId === "uniformat" || colId === "masterformat") {
    const system = colId === "uniformat" ? "UNIFORMAT" : "MASTERFORMAT";
    const next = row.classifications.find((item) => item.system === system);
    const previous = event.oldValue as { code?: string } | null;
    const nextCode = next?.code ?? "";
    if (nextCode === (previous?.code ?? "")) return null;
    return colId === "uniformat"
      ? { uniformat: nextCode }
      : { masterformat: nextCode };
  }
  return null;
}

function revertCatalogItemCell(
  item: DesignScopeCatalogItem,
  colId: string,
  oldValue: unknown
): DesignScopeCatalogItem {
  if (colId === "title") return { ...item, title: String(oldValue ?? "") };
  if (colId === "description") {
    return { ...item, description: String(oldValue ?? "") };
  }
  if (colId === "uniformat" || colId === "masterformat") {
    const system = colId === "uniformat" ? "UNIFORMAT" : "MASTERFORMAT";
    const rest = item.classifications.filter(
      (entry) => entry.system !== system
    );
    const previous = oldValue as DesignScopeCatalogItemClassification | null;
    return {
      ...item,
      classifications: previous?.code
        ? [
            ...rest,
            {
              id: previous.id,
              name: previous.name ?? "",
              code: previous.code,
              system
            }
          ]
        : rest
    };
  }
  return item;
}

function revertCatalogItemInList(
  items: DesignScopeCatalogItem[],
  rowId: string,
  colId: string,
  oldValue: unknown
): DesignScopeCatalogItem[] {
  return items.map((item) =>
    item.id === rowId ? revertCatalogItemCell(item, colId, oldValue) : item
  );
}

function classificationForSystem(
  classifications: DesignScopeCatalogItemClassification[] | undefined,
  system: DesignScopeCatalogItemClassification["system"]
): DesignScopeCatalogItemClassification | null {
  return classifications?.find((item) => item.system === system) ?? null;
}

function classificationFilterText(
  classifications: DesignScopeCatalogItemClassification[] | undefined,
  system: DesignScopeCatalogItemClassification["system"]
): string {
  const matches =
    classifications?.filter((item) => item.system === system) ?? [];
  return matches
    .map((item) => [item.code, item.name].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ");
}

function setClassificationForSystem(
  data: ScopeItemRow,
  system: DesignScopeCatalogItemClassification["system"],
  picked: ClassificationSearchSelection | null
): boolean {
  const rest = data.classifications.filter((item) => item.system !== system);
  if (!picked?.code && !picked?.name) {
    if (rest.length === data.classifications.length) return false;
    data.classifications = rest;
    return true;
  }
  data.classifications = [
    ...rest,
    {
      id: picked.id,
      name: picked.name,
      code: picked.code,
      system
    }
  ];
  return true;
}

type AddScopeItemDraft = {
  title: string;
  description: string;
  uniformat: ClassificationSearchSelection | null;
  masterformat: ClassificationSearchSelection | null;
};

const emptyAddScopeItemDraft = (): AddScopeItemDraft => ({
  title: "",
  description: "",
  uniformat: null,
  masterformat: null
});

type AddFormColumn = {
  id: string;
  width: number;
};

function readDisplayedColumns(
  api: GridApi<ScopeItemRow> | undefined
): AddFormColumn[] {
  return (
    api?.getAllDisplayedColumns()?.map((column) => ({
      id: column.getColId(),
      width: column.getActualWidth()
    })) ?? []
  );
}

function addFormSlotClassName(columnId: string) {
  if (columnId === "actions") {
    return "flex items-center justify-end gap-0.5 px-2";
  }
  if (columnId === "displayId") {
    return "px-3";
  }
  if (
    columnId === "title" ||
    columnId === "description" ||
    columnId === "uniformat" ||
    columnId === "masterformat"
  ) {
    return "pl-3 pr-4";
  }
  return undefined;
}

function AddScopeItemPanel({
  columns,
  draft,
  saving,
  onDraftChange,
  onSave,
  onCancel
}: {
  columns: AddFormColumn[];
  draft: AddScopeItemDraft;
  saving: boolean;
  onDraftChange: (next: AddScopeItemDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const titleRef = useRef<InputRef>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCancel();
    };
    panel.addEventListener("keydown", onKeyDown);
    return () => panel.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const fieldFor = (columnId: string) => {
    switch (columnId) {
      case "displayId":
        return (
          <span
            className="flex h-8 items-center text-sm text-ciq-gray-800"
            data-testid="catalog-items-add-id"
          >
            -
          </span>
        );
      case "title":
        return (
          <Input
            ref={titleRef}
            value={draft.title}
            className="w-full"
            placeholder="Title"
            data-testid="catalog-items-add-title"
            onChange={(event) =>
              onDraftChange({ ...draft, title: event.target.value })
            }
          />
        );
      case "description":
        return (
          <Input.TextArea
            value={draft.description}
            className="w-full"
            placeholder="Description"
            autoSize={{ minRows: 2, maxRows: 4 }}
            data-testid="catalog-items-add-description"
            onChange={(event) =>
              onDraftChange({ ...draft, description: event.target.value })
            }
          />
        );
      case "uniformat":
        return (
          <ClassificationSearchSelect
            system="UF"
            value={draft.uniformat}
            testId="catalog-items-add-uniformat"
            onChange={(uniformat) => onDraftChange({ ...draft, uniformat })}
          />
        );
      case "masterformat":
        return (
          <ClassificationSearchSelect
            system="MF"
            value={draft.masterformat}
            testId="catalog-items-add-masterformat"
            onChange={(masterformat) =>
              onDraftChange({ ...draft, masterformat })
            }
          />
        );
      case "actions":
        return (
          <>
            <Button
              type="link"
              icon={<CheckOutlined />}
              className="!h-6 !w-6 !min-w-6 !border-none !bg-transparent !px-0 !shadow-none hover:!border-none hover:!bg-transparent hover:!text-ciq-primary-900"
              aria-label="Save scope item"
              data-testid="catalog-items-add-save"
              disabled={saving || !draft.title.trim()}
              onClick={onSave}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              className="!h-6 !w-6 !min-w-6 !border-none !bg-transparent !px-0 !shadow-none text-ciq-gray-950 hover:!border-none hover:!bg-transparent hover:!text-ciq-gray-950"
              aria-label="Cancel add scope item"
              data-testid="catalog-items-add-cancel"
              onClick={onCancel}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={panelRef}
      className="absolute inset-x-0 bottom-0 z-10 flex items-start border-0 border-t border-solid border-ciq-gray-200 bg-ciq-gray-25 pb-2 pt-4"
      data-testid="catalog-items-add-panel"
    >
      {columns.map((column) => (
        <div
          key={column.id}
          className={addFormSlotClassName(column.id)}
          style={{ width: column.width }}
        >
          {fieldFor(column.id)}
        </div>
      ))}
    </div>
  );
}

function InlineCatalogText({
  value,
  required,
  textClassName,
  emptyLabel,
  trailing,
  inputClassName = "max-w-lg",
  inputTestId,
  editTestId,
  saveTestId,
  cancelTestId,
  onSave
}: {
  value: string;
  required: boolean;
  textClassName: string;
  emptyLabel?: string;
  trailing?: ReactNode;
  /** Width classes for the edit input. Defaults to max-w-lg (title). */
  inputClassName?: string;
  inputTestId: string;
  editTestId: string;
  saveTestId: string;
  cancelTestId: string;
  onSave: (next: string) => void | boolean | Promise<void | boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<InputRef>(null);
  const trimmed = draft.trim();
  const canSave = !required || trimmed.length > 0;

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
  }, [editing]);

  const startEditing = () => {
    setDraft(value);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const saved = await onSave(trimmed);
      if (saved === false) return;
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        className={inputClassName}
        data-testid={inputTestId}
        onChange={(event) => setDraft(event.target.value)}
        onPressEnter={save}
        onKeyDown={(event) => {
          if (event.key === "Escape") cancel();
        }}
        suffix={
          <span className="flex items-center gap-1">
            <button
              type="button"
              className="m-0 cursor-pointer border-0 bg-transparent p-0 text-ciq-gray-950 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Save"
              data-testid={saveTestId}
              disabled={!canSave || saving}
              onMouseDown={(event) => event.preventDefault()}
              onClick={save}
            >
              <CheckOutlined />
            </button>
            <button
              type="button"
              className="m-0 cursor-pointer border-0 bg-transparent p-0 text-ciq-gray-950"
              aria-label="Cancel"
              data-testid={cancelTestId}
              onMouseDown={(event) => event.preventDefault()}
              onClick={cancel}
            >
              <CloseOutlined />
            </button>
          </span>
        }
      />
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className={`min-w-0 ${textClassName}`} title={value || undefined}>
        {value ? (
          value
        ) : emptyLabel ? (
          <button
            type="button"
            className="m-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-normal text-ciq-gray-800/60"
            data-testid={`${editTestId}-placeholder`}
            onClick={startEditing}
          >
            {emptyLabel}
          </button>
        ) : null}
      </span>
      {trailing}
      <Button
        type="text"
        size="small"
        className="shrink-0 !border-none !bg-transparent text-ciq-gray-800 hover:!bg-transparent"
        icon={<EditOutlined />}
        aria-label={`Edit ${required ? "title" : "description"}`}
        data-testid={editTestId}
        onClick={startEditing}
      />
    </span>
  );
}

const catalogItemsGridClassName =
  "ag-theme-alpine [--ag-border-color:var(--ciq-gray-200)] [--ag-cell-horizontal-padding:16px] [--ag-header-background-color:var(--ciq-white)] [--ag-header-foreground-color:var(--ciq-black-a87)] [--ag-row-border-color:var(--ciq-gray-200)] [&_.ag-header-cell-label]:font-bold [&_.ag-cell]:leading-[1.4] [&_.ag-root-wrapper]:!border-0";

function startCellEdit(params: ICellRendererParams<ScopeItemRow>) {
  const rowIndex = params.node?.rowIndex;
  const colKey = params.column?.getColId() ?? params.colDef?.colId;
  if (rowIndex == null || !colKey) return;
  // Same sequence as the submittal and schedule logs: focus the cell, then
  // start editing. Click and double-click are blocked on the grid, so this
  // is the only way a cell enters edit mode.
  params.api.setFocusedCell(rowIndex, colKey);
  params.api.startEditingCell({ rowIndex, colKey });
}

function EditableTextCell(params: ICellRendererParams<ScopeItemRow, string>) {
  // Hierarchy group rows have no item data — no edit affordance.
  if (!params.data) return null;
  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => startCellEdit(params)}
    >
      <TruncatedTextWithTooltip
        text={params.value ?? ""}
        dataTestId={params.colDef?.colId}
      />
    </CellChilderenRendererWithEditButton>
  );
}

function DeleteItemCell({ data, context }: ICellRendererParams<ScopeItemRow>) {
  if (!data) return null;
  const onDeleteItem = (
    context as { onDeleteItem?: (row: ScopeItemRow) => void } | undefined
  )?.onDeleteItem;
  return (
    <div className="flex w-full items-center justify-end">
      <Button
        type="text"
        danger
        size="small"
        icon={<DeleteOutlined />}
        aria-label={`Delete ${data.title}`}
        data-testid={`catalog-item-delete-${data.id}`}
        onClick={() => onDeleteItem?.(data)}
      />
    </div>
  );
}

function ClassificationCell(
  params: ICellRendererParams<ScopeItemRow> & {
    system: "UNIFORMAT" | "MASTERFORMAT";
  }
) {
  const { data, system } = params;
  if (!data) return null;
  const badgeSystem = system === "UNIFORMAT" ? "UF" : "MF";
  const matches = data.classifications.filter((item) => item.system === system);
  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => startCellEdit(params)}
    >
      {matches.length === 0 ? (
        <Typography.Text type="secondary">—</Typography.Text>
      ) : (
        matches.map((item) => (
          <span
            key={item.id}
            className="inline-flex min-w-0 items-center gap-2"
          >
            <ClassBadge system={badgeSystem} code={item.code} />
            <Typography.Text className="min-w-0" ellipsis>
              {item.name || "—"}
            </Typography.Text>
          </span>
        ))
      )}
    </CellChilderenRendererWithEditButton>
  );
}

/** Shared UniFormat / MasterFormat column — list cells and hierarchy leaf edits. */
function buildClassificationColumnDef(
  headerName: string,
  colId: "uniformat" | "masterformat",
  storedSystem: "UNIFORMAT" | "MASTERFORMAT",
  searchSystem: "UF" | "MF"
): ColDef<ScopeItemRow> {
  return {
    headerName,
    colId,
    flex: 2,
    minWidth: 220,
    maxWidth: 320,
    editable: true,
    cellClass: "ciq-cell-v-center cell-editable",
    cellEditor: ClassificationSearchCellEditor,
    cellEditorPopup: false,
    cellEditorParams: { system: searchSystem },
    suppressKeyboardEvent: (params) =>
      params.editing &&
      ["ArrowUp", "ArrowDown", "Enter"].includes(params.event.key),
    valueGetter: (params) =>
      classificationForSystem(params.data?.classifications, storedSystem),
    valueFormatter: (params) =>
      classificationFilterText(params.data?.classifications, storedSystem),
    filter: "agSetColumnFilter",
    filterValueGetter: (params) =>
      classificationFilterText(params.data?.classifications, storedSystem),
    menuTabs: ["filterMenuTab"],
    valueSetter: (params: ValueSetterParams<ScopeItemRow>) =>
      setClassificationForSystem(
        params.data,
        storedSystem,
        params.newValue as ClassificationSearchSelection | null
      ),
    cellRenderer: (params: ICellRendererParams<ScopeItemRow>) => (
      <ClassificationCell {...params} system={storedSystem} />
    )
  };
}

function CatalogItemsView({
  catalog,
  onBack,
  onCatalogUpdated
}: CatalogItemsViewProps) {
  const { eventLogs = [], isFeatureFlagEnabled = () => false } =
    useContext(SubscriptionContext);
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");
  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"list" | "hierarchy">("list");
  const [treeSystem, setTreeSystem] = useState<CatalogTreeSystem>("UF");
  const [hierarchyMenuOpen, setHierarchyMenuOpen] = useState(false);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [addingItemSaving, setAddingItemSaving] = useState(false);
  const [addDraft, setAddDraft] = useState<AddScopeItemDraft>(
    emptyAddScopeItemDraft
  );
  const [addColumns, setAddColumns] = useState<AddFormColumn[]>([]);
  const itemsGridRef = useRef<AgGridReact<ScopeItemRow>>(null);
  const loadRequestId = useRef(0);
  const apolloClient = useApolloClient();
  const { saveAuditLogEvent } = useAuditEventLog(apolloClient);
  const [items, setItems] = useState<DesignScopeCatalogItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [exportingItems, setExportingItems] = useState(false);
  const [itemPendingDelete, setItemPendingDelete] =
    useState<ScopeItemRow | null>(null);
  const [deletingItem, setDeletingItem] = useState(false);

  const hierarchy = view === "hierarchy";
  const treeExpandToggle = useAgGridTreeExpandToggle({
    gridRef: itemsGridRef,
    enabled: hierarchy,
    resetKey: `${view}-${treeSystem}`,
    initiallyAllExpanded: true
  });

  const loadItems = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      const requestId = ++loadRequestId.current;
      if (!silent) setLoadingItems(true);
      const response = await listDesignScopeCatalogItems(
        apolloClient,
        catalog.id
      );
      if (requestId !== loadRequestId.current) return;
      if (!response.success || !Array.isArray(response.data)) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        setItems([]);
      } else {
        setItems(response.data as DesignScopeCatalogItem[]);
      }
      // Always clear: a silent refresh may have superseded one that showed the spinner.
      setLoadingItems(false);
    },
    [apolloClient, catalog.id]
  );

  useEffect(() => {
    setAddingItem(false);
    setAddDraft(emptyAddScopeItemDraft());
    loadItems();
  }, [loadItems]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      const shouldReloadItems = eventLogs.some(
        (log) =>
          log.data_source === "feature_catalog_item" ||
          (log.data_source === "feature_instance" &&
            log.change_type === "import")
      );
      if (shouldReloadItems) {
        loadItems({ silent: true });
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, loadItems]);

  const visibleRows = useMemo<ScopeItemRow[]>(() => {
    const rows = items.map((item, index) => ({
      ...item,
      displayId: index + 1
    }));
    const query = search.trim().toLowerCase();
    const filtered = !query
      ? rows
      : rows.filter(
          (row) =>
            row.title.toLowerCase().includes(query) ||
            row.description.toLowerCase().includes(query)
        );
    if (!hierarchy) return filtered;
    // Keep Unclassified at the bottom of the unsorted tree.
    return [...filtered].sort((a, b) => {
      const last = (row: ScopeItemRow) =>
        catalogItemDataPath(row, treeSystem)[0] === CATALOG_UNCLASSIFIED
          ? 1
          : 0;
      return last(a) - last(b);
    });
  }, [hierarchy, items, search, treeSystem]);

  const getDataPath = useCallback(
    (data: ScopeItemRow) => catalogItemDataPath(data, treeSystem),
    [treeSystem]
  );

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<ScopeItemRow>) => {
      const rawColId = event.column.getColId();
      // Hierarchy tree-axis edits use the auto-group column; map to UF/MF.
      const colId =
        rawColId === "ag-Grid-AutoColumn" ||
        rawColId.startsWith("ag-Grid-AutoColumn")
          ? treeSystem === "MF"
            ? "masterformat"
            : "uniformat"
          : rawColId;
      const row = event.data;
      if (!row) return;
      const patch = catalogItemPatchForCell(colId, event);
      setItems((current) =>
        current.map((item) =>
          item.id === row.id
            ? {
                ...row,
                title: row.title,
                description: row.description,
                classifications: row.classifications
              }
            : item
        )
      );
      if (!patch) return;

      void (async () => {
        const response = await updateDesignScopeCatalogItem(
          apolloClient,
          catalog.id,
          row.id,
          patch
        );
        if (!response.success) {
          message.error(designScopeActionErrorMessage(response.errorMsg));
          setItems((current) =>
            revertCatalogItemInList(current, row.id, colId, event.oldValue)
          );
          return;
        }
        message.success(scopeItemUpdatedMessage(colId));
        // Classification updates need fresh path/parent_id for hierarchy.
        if (colId === "uniformat" || colId === "masterformat") {
          await loadItems({ silent: true });
        }
      })();
    },
    [apolloClient, catalog.id, loadItems, treeSystem]
  );

  const syncAddColumns = useCallback(() => {
    if (!addingItem) return;
    setAddColumns(readDisplayedColumns(itemsGridRef.current?.api));
  }, [addingItem]);

  const toggleAddScopeItem = useCallback(() => {
    if (addingItem) {
      setAddingItem(false);
      setAddDraft(emptyAddScopeItemDraft());
      return;
    }
    setAddColumns(readDisplayedColumns(itemsGridRef.current?.api));
    setAddDraft(emptyAddScopeItemDraft());
    setAddingItem(true);
  }, [addingItem]);

  const closeAddScopeItem = useCallback(() => {
    setAddingItem(false);
    setAddDraft(emptyAddScopeItemDraft());
  }, []);

  useEffect(() => {
    if (hierarchy) closeAddScopeItem();
  }, [hierarchy, closeAddScopeItem]);

  const saveNewScopeItem = useCallback(async () => {
    const title = addDraft.title.trim();
    if (!title || addingItemSaving) return;

    const description = addDraft.description.trim();
    setAddingItemSaving(true);
    try {
      const response = await addDesignScopeCatalogItem(
        apolloClient,
        catalog.id,
        {
          title,
          ...(description ? { description } : {}),
          ...(addDraft.uniformat?.code
            ? { uniformat: addDraft.uniformat.code }
            : {}),
          ...(addDraft.masterformat?.code
            ? { masterformat: addDraft.masterformat.code }
            : {})
        }
      );
      if (!response.success) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return;
      }
      message.success(DESIGN_SCOPE_ITEM_ADDED);
      closeAddScopeItem();
      await loadItems();
    } finally {
      setAddingItemSaving(false);
    }
  }, [
    addDraft.description,
    addDraft.masterformat?.code,
    addDraft.title,
    addDraft.uniformat?.code,
    addingItemSaving,
    apolloClient,
    catalog.id,
    closeAddScopeItem,
    loadItems
  ]);

  const requestDeleteItem = useCallback((row: ScopeItemRow) => {
    setItemPendingDelete(row);
  }, []);

  const confirmDeleteItem = useCallback(async () => {
    if (!itemPendingDelete || deletingItem) return;
    setDeletingItem(true);
    try {
      const response = await deleteDesignScopeCatalogItem(
        apolloClient,
        catalog.id,
        itemPendingDelete.id
      );
      if (!response.success) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return;
      }
      message.success(DESIGN_SCOPE_ITEM_DELETED);
      const deletedId = itemPendingDelete.id;
      setItems((current) => current.filter((item) => item.id !== deletedId));
      setItemPendingDelete(null);
    } finally {
      setDeletingItem(false);
    }
  }, [apolloClient, catalog.id, deletingItem, itemPendingDelete]);

  const refreshItems = useCallback(() => {
    loadItems();
  }, [loadItems]);

  const saveCatalog = useCallback(
    async (patch: { name?: string; description?: string | null }) => {
      const response = await updateDesignScopeCatalog(
        apolloClient,
        catalog.id,
        patch
      );
      if (!response.success) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return false;
      }

      message.success(
        patch.name !== undefined
          ? DESIGN_SCOPE_LIST_TITLE_UPDATED
          : DESIGN_SCOPE_LIST_DESCRIPTION_UPDATED
      );
      const updated = response.data as
        | { name?: string; description?: string | null }
        | undefined;
      onCatalogUpdated({
        name:
          patch.name !== undefined
            ? (updated?.name ?? patch.name)
            : catalog.name,
        description:
          patch.description !== undefined
            ? (updated?.description ?? patch.description)
            : catalog.description
      });
      return true;
    },
    [
      apolloClient,
      catalog.description,
      catalog.id,
      catalog.name,
      onCatalogUpdated
    ]
  );

  const exportCatalogItems = useCallback(async () => {
    if (exportingItems) return;
    setExportingItems(true);
    try {
      const response = await downloadDesignScopeImportTemplate();
      if (!response.success || !(response.data instanceof Blob)) {
        message.error(designScopeActionErrorMessage(response.errorMsg));
        return;
      }

      const templateText = await response.data.text();
      const instructionLines = instructionLinesFromImportTemplate(templateText);
      const filename = designScopeItemsExportFilename(catalog.name);

      downloadStringAsCSVFile(
        buildDesignScopeItemsCsv(items, instructionLines),
        filename
      );
      await saveAuditLogEvent({
        eventType: designScopeCatalogExportEventType("CSV", "list", treeSystem),
        featureType: "DESIGN_SCOPE",
        filename
      });
    } catch {
      message.error(DESIGN_SCOPE_ACTION_ERROR);
    } finally {
      setExportingItems(false);
    }
  }, [catalog.name, exportingItems, items, saveAuditLogEvent, treeSystem]);

  const onExportExcel = useCallback(
    (options: ExcelExportOptions) => {
      if (!itemsGridRef.current) return;
      const filename = `Design_Scope_Catalog_${sanitizeSheetName(
        catalog.name
      )}.xlsx`;
      exportEntityLogAsExcel({
        projectDetails: { name: catalog.name } as ProjectSettingType,
        gridRef: itemsGridRef,
        filename,
        prependText: `${
          catalog.name
        } - Design Scope Catalog - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        excludedColumns: CATALOG_EXPORT_EXCLUDED_COLUMNS,
        options
      });
      saveAuditLogEvent({
        eventType: designScopeCatalogExportEventType(
          "EXCEL",
          hierarchy ? "hierarchy" : "list",
          treeSystem
        ),
        featureType: "DESIGN_SCOPE",
        filename
      });
    },
    [catalog.name, hierarchy, saveAuditLogEvent, treeSystem]
  );

  const onOpenPrintView = useCallback(() => {
    if (!itemsGridRef.current) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      sessionStorage.clear();
      const pdfFileBaseName = designScopeCatalogPdfFileBaseName(catalog.name);
      const printViewState: DesignScopeCatalogPrintViewState = {
        pageId: "design-scope-catalog",
        title: `${
          catalog.name || "Catalog"
        } - Design Scope Catalog - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState: itemsGridRef.current.api.getState(),
        context: {},
        skipColumnIds: CATALOG_EXPORT_EXCLUDED_COLUMNS,
        headerHeight: itemsGridRef.current.props?.headerHeight,
        rowHeight: undefined,
        catalogId: catalog.id,
        catalogName: catalog.name,
        hierarchy,
        treeSystem,
        pdfFileBaseName
      };
      sessionStorage.setItem(
        PRINT_VIEW_STORAGE_KEY,
        JSON.stringify(printViewState)
      );
      window.open("/reports/printView", "_blank");
    } catch (error) {
      console.error("Error opening design scope catalog print view:", error);
      message.error("An error occurred while opening print view.");
    }
  }, [catalog.id, catalog.name, hierarchy, treeSystem]);

  const openPrintValidation = useCallback(() => {
    if (!itemsGridRef.current) return;
    PrintViewValidationModal({
      gridApi: itemsGridRef.current.api,
      onProceed: onOpenPrintView,
      skipColumnIds: CATALOG_EXPORT_EXCLUDED_COLUMNS
    });
  }, [onOpenPrintView]);

  const exportOptions = useMemo(() => {
    const options: ExportFormatOption[] = [];
    if (!hierarchy) {
      options.push({
        key: "export_csv",
        title: "CSV",
        testId: "catalog-items-export-csv"
      });
    }
    if (isExcelExportEnabled) {
      options.push({
        key: "export_excel",
        title: "Excel (.xlsx)",
        isExcel: true,
        testId: "catalog-items-export-excel"
      });
    }
    if (isPrintViewEnabled) {
      options.push({
        key: "print_view",
        title: "PDF/Print",
        testId: "catalog-items-export-pdf"
      });
    }
    return options;
  }, [hierarchy, isExcelExportEnabled, isPrintViewEnabled]);

  const onProceedExport = useCallback(
    ({ key, excelOptions }: ExportSelection) => {
      if (key === "export_excel") {
        if (excelOptions) onExportExcel(excelOptions);
        return;
      }
      if (key === "print_view") {
        openPrintValidation();
        return;
      }
      if (key === "export_csv") {
        void exportCatalogItems();
      }
    },
    [exportCatalogItems, onExportExcel, openPrintValidation]
  );

  const columnDefs = useMemo<ColDef<ScopeItemRow>[]>(() => {
    const idColumn: ColDef<ScopeItemRow> = {
      headerName: "ID",
      field: "displayId",
      colId: "displayId",
      width: 72,
      minWidth: 64,
      maxWidth: 96,
      flex: 0,
      suppressHeaderMenuButton: true
    };
    const titleColumn: ColDef<ScopeItemRow> = {
      headerName: "Title",
      field: "title",
      colId: "title",
      flex: 2,
      minWidth: 180,
      maxWidth: 360,
      editable: (params) => Boolean(params.data),
      cellClass: (params) =>
        params.data ? "ciq-cell-v-center cell-editable" : "ciq-cell-v-center",
      cellEditor: AntdTextCellEditor,
      cellEditorPopup: false,
      cellRenderer: EditableTextCell,
      filter: true,
      menuTabs: ["filterMenuTab"],
      valueSetter: (params) => {
        if (!params.data) return false;
        const next = String(params.newValue ?? "").trim();
        if (!next) return false;
        params.data.title = next;
        return true;
      }
    };
    const descriptionColumn: ColDef<ScopeItemRow> = {
      headerName: "Description",
      field: "description",
      colId: "description",
      flex: 1.87,
      minWidth: 154,
      maxWidth: 362,
      editable: (params) => Boolean(params.data),
      cellClass: (params) =>
        params.data ? "ciq-cell-v-center cell-editable" : "ciq-cell-v-center",
      cellEditor: AntdTextCellEditor,
      cellEditorPopup: false,
      cellRenderer: EditableTextCell,
      valueSetter: (params) => {
        if (!params.data) return false;
        params.data.description = String(params.newValue ?? "").trim();
        return true;
      },
      suppressHeaderMenuButton: true
    };
    const ufColumn = buildClassificationColumnDef(
      "UniFormat",
      "uniformat",
      "UNIFORMAT",
      "UF"
    );
    const mfColumn = buildClassificationColumnDef(
      "MasterFormat",
      "masterformat",
      "MASTERFORMAT",
      "MF"
    );
    const actionsColumn: ColDef<ScopeItemRow> = {
      headerName: "Actions",
      colId: "actions",
      width: 88,
      minWidth: 72,
      maxWidth: 120,
      flex: 0,
      sortable: false,
      resizable: false,
      pinned: "right",
      lockPosition: "right",
      suppressMovable: true,
      headerClass: "[&_.ag-header-cell-label]:!justify-end",
      cellClass: "ciq-cell-v-center",
      cellRenderer: DeleteItemCell,
      suppressHeaderMenuButton: true
    };
    const scopeItemCountColumn: ColDef<ScopeItemRow> = {
      headerName: "No. of Scope Items",
      colId: "scopeItemCount",
      suppressMovable: true,
      minWidth: 160,
      maxWidth: 200,
      filter: false,
      valueGetter: (params) => {
        if (!params.node || params.node.group !== true) return "";
        return (params.node.allLeafChildren ?? []).filter((n) => n.data).length;
      },
      cellRenderer: (params: ICellRendererParams<ScopeItemRow>) => {
        if (!params.node?.group) return null;
        const count = (params.node.allLeafChildren ?? []).filter(
          (n) => n.data
        ).length;
        return (
          <span className="inline-flex items-center rounded-xl border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs font-normal text-black text-opacity-50">
            {count}
          </span>
        );
      }
    };

    if (!hierarchy) {
      return [
        idColumn,
        titleColumn,
        descriptionColumn,
        ufColumn,
        mfColumn,
        actionsColumn
      ];
    }
    const otherFormat = treeSystem === "UF" ? mfColumn : ufColumn;
    return [
      scopeItemCountColumn,
      idColumn,
      titleColumn,
      descriptionColumn,
      otherFormat,
      actionsColumn
    ];
  }, [hierarchy, treeSystem]);

  const toggleGroupOneLevel = useCallback((node: IRowNode<ScopeItemRow>) => {
    const expanded = !node.expanded;
    if (expanded) {
      node.setExpanded(true);
      descendantGroupNodes(node).forEach((child) => child.setExpanded(true));
      return;
    }
    [...descendantGroupNodes(node)]
      .reverse()
      .forEach((child) => child.setExpanded(false));
    node.setExpanded(false);
  }, []);

  const autoGroupColumnDef = useMemo<ColDef<ScopeItemRow> | undefined>(() => {
    if (!hierarchy) return undefined;
    // Same editor/renderer/setter as the list UniFormat / MasterFormat columns.
    const treeAxisColumn =
      treeSystem === "UF"
        ? buildClassificationColumnDef(
            "UniFormat",
            "uniformat",
            "UNIFORMAT",
            "UF"
          )
        : buildClassificationColumnDef(
            "MasterFormat",
            "masterformat",
            "MASTERFORMAT",
            "MF"
          );
    const stored =
      treeSystem === "UF" ? ("UNIFORMAT" as const) : ("MASTERFORMAT" as const);
    return {
      headerName: CATALOG_TREE_SYSTEM_LABEL[treeSystem],
      pinned: "left",
      minWidth: 420,
      width: 420,
      flex: 2,
      // Leaf scope-item rows only — classification group headers stay read-only.
      editable: (params) => Boolean(params.data),
      cellClass: (params) =>
        params.data
          ? String(
              treeAxisColumn.cellClass ?? "ciq-cell-v-center cell-editable"
            )
          : "ciq-cell-v-center",
      cellEditor: treeAxisColumn.cellEditor,
      cellEditorPopup: treeAxisColumn.cellEditorPopup,
      cellEditorParams: treeAxisColumn.cellEditorParams,
      suppressKeyboardEvent: treeAxisColumn.suppressKeyboardEvent,
      valueSetter: treeAxisColumn.valueSetter,
      // Same set filter as list-view UniFormat / MasterFormat columns.
      filter: treeAxisColumn.filter,
      filterValueGetter: treeAxisColumn.filterValueGetter,
      menuTabs: treeAxisColumn.menuTabs,
      valueGetter: (params) => {
        if (params.data) {
          return typeof treeAxisColumn.valueGetter === "function"
            ? treeAxisColumn.valueGetter(params as never)
            : null;
        }
        const key = String(params.node?.key ?? "");
        if (key === CATALOG_UNCLASSIFIED) return "Unclassified";
        const seg = decodeCatalogSeg(key);
        return [seg.code, seg.title].filter(Boolean).join(" ");
      },
      valueFormatter: (params) => {
        if (!params.data) return String(params.value ?? "");
        return typeof treeAxisColumn.valueFormatter === "function"
          ? String(treeAxisColumn.valueFormatter(params as never) ?? "")
          : String(params.value ?? "");
      },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: ICellRendererParams<ScopeItemRow>) => {
          // Leaves: badge-only (no title) + same edit affordance/editor as list.
          if (params.data) {
            const own = classificationForSystem(
              params.data.classifications,
              stored
            );
            return (
              <CellChilderenRendererWithEditButton
                onEditCell={() => startCellEdit(params)}
              >
                {own?.code ? (
                  <ClassBadge system={treeSystem} code={own.code} />
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )}
              </CellChilderenRendererWithEditButton>
            );
          }
          if (!params.node) return null;
          const key = String(params.node.key ?? "");
          const toggle = (
            <CatalogGroupDepthToggle
              node={params.node}
              onToggleOneLevel={toggleGroupOneLevel}
            />
          );
          if (key === CATALOG_UNCLASSIFIED) {
            return (
              <span className="inline-flex items-center gap-2">
                {toggle}
                <Typography.Text strong type="secondary">
                  Unclassified
                </Typography.Text>
              </span>
            );
          }
          const seg = decodeCatalogSeg(key);
          return (
            <span className="inline-flex min-w-0 items-center gap-2">
              {toggle}
              <ClassBadge system={seg.system} code={seg.code} />
              <Typography.Text strong className="min-w-0" ellipsis>
                {seg.title}
              </Typography.Text>
            </span>
          );
        }
      }
    };
  }, [hierarchy, toggleGroupOneLevel, treeSystem]);

  const hierarchyViewMenu: MenuProps = useMemo(
    () => ({
      selectable: true,
      selectedKeys: hierarchy ? [treeSystem] : [],
      items: [
        {
          key: "UF",
          label: "UniFormat",
          onClick: () => {
            setView("hierarchy");
            setTreeSystem("UF");
            setHierarchyMenuOpen(false);
          }
        },
        {
          key: "MF",
          label: "MasterFormat",
          onClick: () => {
            setView("hierarchy");
            setTreeSystem("MF");
            setHierarchyMenuOpen(false);
          }
        }
      ]
    }),
    [hierarchy, treeSystem]
  );

  const hierarchySegmentLabel = hierarchy
    ? CATALOG_TREE_SYSTEM_LABEL[treeSystem]
    : "Hierarchy View";

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      menuTabs: [],
      cellClass: "ciq-cell-v-center"
    }),
    []
  );

  const getRowId = useCallback(
    (params: { data: ScopeItemRow }) => params.data.id,
    []
  );

  return (
    <div
      className="catalog-items-view flex h-full min-h-0 flex-col px-8 pt-6"
      data-testid="catalog-items-view"
    >
      <div className="flex min-w-0 items-start gap-2">
        <Button
          type="text"
          className="mt-0.5 shrink-0 !border-none !bg-transparent px-1 text-ciq-gray-800 hover:!bg-transparent"
          icon={<ArrowLeftOutlined />}
          aria-label="Back to catalogs"
          data-testid="catalog-items-back"
          onClick={onBack}
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <InlineCatalogText
              value={catalog.name}
              required
              textClassName="truncate text-lg font-semibold text-ciq-gray-950"
              trailing={
                <span
                  className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 px-1 text-xs text-ciq-gray-800"
                  data-testid="catalog-items-count"
                >
                  {items.length}
                </span>
              }
              inputTestId="catalog-items-title-input"
              editTestId="catalog-items-title-edit"
              saveTestId="catalog-items-title-save"
              cancelTestId="catalog-items-title-cancel"
              onSave={(name) => {
                if (name === catalog.name) return true;
                return saveCatalog({ name });
              }}
            />
          </div>
          <div className="mt-1 w-[60vw] min-w-80 max-w-[60vw]">
            <InlineCatalogText
              value={catalog.description ?? ""}
              required={false}
              textClassName="truncate text-sm text-ciq-gray-800"
              emptyLabel="Add description"
              inputClassName="w-full min-w-80 max-w-full"
              inputTestId="catalog-items-description-input"
              editTestId="catalog-items-description-edit"
              saveTestId="catalog-items-description-save"
              cancelTestId="catalog-items-description-cancel"
              onSave={(description) => {
                const next = description.trim();
                if (next === (catalog.description ?? "").trim()) return true;
                return saveCatalog({ description: next });
              }}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Segmented
            data-testid="catalog-items-view-toggle"
            value={view}
            onChange={(next) => {
              if (next === "list") {
                setView("list");
                setHierarchyMenuOpen(false);
                return;
              }
              setHierarchyMenuOpen(true);
            }}
            options={[
              {
                value: "list",
                label: (
                  <span data-testid="catalog-items-list-view">List View</span>
                )
              },
              {
                value: "hierarchy",
                label: (
                  <Dropdown
                    open={hierarchyMenuOpen}
                    onOpenChange={setHierarchyMenuOpen}
                    menu={hierarchyViewMenu}
                    trigger={["click"]}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      className="inline-flex items-center gap-1"
                      data-testid="catalog-items-hierarchy-view"
                      onClick={() => setHierarchyMenuOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setHierarchyMenuOpen(true);
                        }
                      }}
                    >
                      {hierarchySegmentLabel}
                      <DownOutlined
                        className="text-xs"
                        data-testid="catalog-items-hierarchy-menu-trigger"
                      />
                    </span>
                  </Dropdown>
                )
              }
            ]}
          />
          {hierarchy && (
            <Button
              type="link"
              className="!text-ciq-primary-950 px-0"
              icon={
                treeExpandToggle.showCollapseAll ? (
                  <ShrinkOutlined />
                ) : (
                  <ArrowsAltOutlined />
                )
              }
              data-testid="catalog-items-hierarchy-tree-toggle"
              onClick={treeExpandToggle.toggleExpandCollapse}
            >
              {treeExpandToggle.showCollapseAll ? "Collapse All" : "Expand All"}
            </Button>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined className="text-ciq-gray-800" />}
            placeholder="Search scope item"
            className="w-56"
            value={search}
            data-testid="catalog-items-search"
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button
            data-testid="catalog-items-import"
            onClick={() => setImportDrawerOpen(true)}
          >
            Import List
          </Button>
          {exportOptions.length > 0 ? (
            <ExportOptionsPopover
              options={exportOptions}
              onProceed={onProceedExport}
              excelTableName={catalog.name || "Catalog"}
              showIncludeComments={false}
              loading={exportingItems}
              buttonType="default"
              buttonTestId="catalog-items-export"
            />
          ) : null}
        </div>
      </div>

      <div className="mt-2 min-h-0 flex-1">
        <div className="flex h-full min-h-0 flex-col overflow-hidden border border-solid border-ciq-gray-200">
          <div className="min-h-0 flex-1">
            <div
              className={`catalog-items-grid relative h-full ${catalogItemsGridClassName}`}
              data-testid="catalog-items-grid"
            >
              <Spin
                spinning={loadingItems}
                description="Loading"
                classNames={{
                  root: "block h-full [&_.ant-spin-container]:h-full"
                }}
              >
                <div className="h-full">
                  <AgGridReact<ScopeItemRow>
                    key={hierarchy ? `hierarchy-${treeSystem}` : "list"}
                    ref={itemsGridRef}
                    rowData={visibleRows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowId}
                    headerHeight={40}
                    animateRows={false}
                    stopEditingWhenCellsLoseFocus
                    suppressClickEdit
                    popupParent={document.body}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu
                    suppressNoRowsOverlay={loadingItems}
                    overlayNoRowsTemplate="No scope items found"
                    context={{ onDeleteItem: requestDeleteItem }}
                    treeData={hierarchy}
                    getDataPath={hierarchy ? getDataPath : undefined}
                    groupDefaultExpanded={hierarchy ? -1 : undefined}
                    postSortRows={
                      hierarchy
                        ? placeCatalogItemsBeforeNestedGroups
                        : undefined
                    }
                    {...(autoGroupColumnDef ? { autoGroupColumnDef } : {})}
                    rowClassRules={{
                      "catalog-items-tree-leaf": (params) =>
                        hierarchy && Boolean(params.data)
                    }}
                    onCellValueChanged={onCellValueChanged}
                    onColumnMoved={syncAddColumns}
                    onColumnResized={syncAddColumns}
                    onGridSizeChanged={syncAddColumns}
                    onRowGroupOpened={
                      hierarchy
                        ? treeExpandToggle.gridHandlers.onRowGroupOpened
                        : undefined
                    }
                    onExpandOrCollapseAll={
                      hierarchy
                        ? treeExpandToggle.gridHandlers.onExpandOrCollapseAll
                        : undefined
                    }
                    onFirstDataRendered={
                      hierarchy
                        ? treeExpandToggle.gridHandlers.onFirstDataRendered
                        : undefined
                    }
                  />
                </div>
              </Spin>
              {!hierarchy && addingItem ? (
                <AddScopeItemPanel
                  columns={addColumns}
                  draft={addDraft}
                  saving={addingItemSaving}
                  onDraftChange={setAddDraft}
                  onSave={() => {
                    saveNewScopeItem();
                  }}
                  onCancel={closeAddScopeItem}
                />
              ) : null}
            </div>
          </div>
          {!hierarchy ? (
            <button
              type="button"
              className="flex h-[42px] w-full shrink-0 cursor-pointer items-center gap-1 border-0 bg-ciq-gray-25 px-2 text-left text-sm text-ciq-primary-900"
              data-testid="catalog-items-add"
              onClick={toggleAddScopeItem}
            >
              <PlusOutlined />
              Add Scope Item
            </button>
          ) : null}
        </div>
      </div>

      <ImportCatalogItemsDrawer
        open={importDrawerOpen}
        catalogName={catalog.name}
        onClose={() => setImportDrawerOpen(false)}
        onImported={refreshItems}
      />
      <Modal
        open={itemPendingDelete != null}
        title="Delete this scope item?"
        okText="Delete"
        cancelText="Cancel"
        closable={!deletingItem}
        mask={{ closable: !deletingItem }}
        keyboard={!deletingItem}
        okButtonProps={{
          danger: true,
          className: "ciq-delete-btn",
          loading: deletingItem,
          "data-testid": "catalog-item-delete-confirm"
        }}
        cancelButtonProps={{
          disabled: deletingItem,
          "data-testid": "catalog-item-delete-cancel"
        }}
        onOk={() => {
          confirmDeleteItem();
        }}
        onCancel={() => {
          if (deletingItem) return;
          setItemPendingDelete(null);
        }}
      >
        <p
          className="m-0 text-ciq-gray-950"
          data-testid="catalog-item-delete-message"
        >
          “{itemPendingDelete?.title}” will be removed from this list.
        </p>
      </Modal>
    </div>
  );
}

export default CatalogItemsView;
